import { TopBannerOnboarding } from '@/components/TopBannerOnboarding';
import { useNavigateToIntakeForm } from '@/hooks/useNavigateToIntakeForm';
import { useSelector } from 'react-redux';
import { selectMaybeClient, selectSession } from '@/model';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { SessionType } from '@shared';

export type OnboardingResultsStatusVariant = 'pending' | 'error';

type OnboardingResultsStatusScreenProps = {
  variant: OnboardingResultsStatusVariant;
  retryCountdown?: number;
  onRefresh: () => void;
};

export function OnboardingResultsStatusScreen({
  variant,
  retryCountdown,
  onRefresh,
}: OnboardingResultsStatusScreenProps) {
  const isPending = variant === 'pending';
  const client = useSelector(selectMaybeClient);
  const { sessionType } = useSelector(selectSession);
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const hasFormReference = !!client?.formReferenceId;
  const isFormCompleted = client?.formReferenceStatus === 'completed';
  const isClientSession = sessionType === SessionType.client;

  const showCompleteFormButton =
    hasFormReference && !isFormCompleted && (isClientSession || useFormProvider);

  const showProgressText = isPending && isFormCompleted;

  return (
    <div id="top-container">
      <div className="flex min-h-[800px] flex-col px-2 pb-[140px] pt-4 md:px-20">
        <TopBannerOnboarding className="px-2 pb-5 md:px-0" />

        <div className="mx-auto w-full max-w-2xl">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-10">
            <div className="flex flex-col items-center gap-5 text-center">
              {isPending ? (
                <div className="relative">
                  <div className="border-primary-100 border-t-primary h-14 w-14 animate-spin rounded-full border-2" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-primary-50 h-8 w-8 rounded-full" />
                  </div>
                </div>
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-100">
                  <svg
                    className="h-7 w-7 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              )}

              <div className="space-y-2">
                <h2
                  className="text-2xl font-bold text-gray-900 md:text-3xl"
                  data-testid={isPending ? 'results-pending-header' : 'results-error-header'}
                >
                  {showCompleteFormButton
                    ? `Please complete your intake form`
                    : isPending
                      ? `We're evaluating your responses`
                      : `We couldn't generate your results yet`}
                </h2>
                <p className="text-base text-gray-700 md:text-lg">
                  {showCompleteFormButton
                    ? `We need your intake form information to generate your long-term care predictions.`
                    : isPending
                      ? `We're generating your long-term care predictions. Please come back again and try after some time.`
                      : `Something went wrong while evaluating your responses. Please try again in a few minutes.`}
                </p>
              </div>

              {showProgressText ? (
                <div className="bg-primary-50 ring-primary-100 w-full rounded-lg p-4 text-left ring-1">
                  <p className="text-primary text-sm">
                    This page will refresh automatically in{' '}
                    <span
                      className="font-semibold"
                      aria-live="polite"
                    >
                      {retryCountdown ?? 0}
                    </span>{' '}
                    seconds.
                  </p>
                </div>
              ) : !showCompleteFormButton && !isPending ? (
                <div className="w-full rounded-lg bg-amber-50 p-4 text-left ring-1 ring-amber-100">
                  <p className="text-sm text-amber-800">
                    If refreshing doesn't help, please check back shortly — our team is
                    automatically notified when this happens.
                  </p>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                {showCompleteFormButton ? (
                  <button
                    onClick={navigateToIntakeForm}
                    disabled={isLoadingForm}
                    className="border-primary text-primary hover:bg-primary-50 focus-visible:outline-primary rounded-md border bg-white px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                    data-testid="complete-intake-form-button"
                  >
                    {isLoadingForm ? 'Loading...' : 'Complete the form'}
                  </button>
                ) : (
                  <button
                    onClick={onRefresh}
                    className="border-primary text-primary hover:bg-primary-50 focus-visible:outline-primary rounded-md border bg-white px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    {isPending ? 'Refresh now' : 'Refresh'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {showProgressText && (
            <p className="mx-auto mt-4 max-w-2xl text-center text-xs text-gray-700">
              You can close this tab and come back later, we'll have your results ready as soon as
              processing finishes.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
